/**
 * Every ordering URL is built here.
 *
 * Route segments are load-bearing in this app: `Drawer`, `TransparentDrawer`
 * and `BackDropProvider` all switch on the pathname (the first two on the last
 * segment, the third on `includes(...)` substrings). A new segment that
 * collides with an existing one silently opens the wrong drawer, so keep the
 * ordering segments below distinct from: bills, pay-your-bill, split-the-bill,
 * equally, custom, pay-for-your-items, divide-bill-equally, leave-tip,
 * extra-tip, confirm-and-pay, checkout-fee, payment-error,
 * success-complete-payment, summary, get-receipt, receipt-sent, feedback.
 */

export const ORDER_SEGMENT = "order";

/**
 * Stands in for a menu type id to mean "everything on the menu", including
 * dishes filed under no menu type and no category, which would otherwise be
 * unreachable. A real menu type id is a UUID, so this can never collide.
 */
export const ALL_MENU_TYPE_ID = "all";

export const tablePath = (restaurantId: string, tableId: string) =>
  `/restaurants/${restaurantId}/tables/${tableId}`;

export const loyaltyPath = (restaurantId: string, tableId: string) =>
  `${tablePath(restaurantId, tableId)}/loyalty`;

/**
 * Tipping on its own. The segment is "tips", not "tip": the gradient Drawer
 * switches on the last path segment and already answers to "tip" with the
 * ordering flow's tip step.
 */
export const tipPath = (restaurantId: string, tableId: string) =>
  `${tablePath(restaurantId, tableId)}/tips`;

export const accountPath = (restaurantId: string, tableId: string) =>
  `${tablePath(restaurantId, tableId)}/account`;

export const allergensPath = (restaurantId: string, tableId: string) =>
  `${accountPath(restaurantId, tableId)}/allergens`;

export const billsPath = (restaurantId: string, tableId: string) =>
  `${tablePath(restaurantId, tableId)}/bills`;

export const menuPath = (restaurantId: string) =>
  `/restaurants/${restaurantId}/menu`;

export const menuTypePath = (restaurantId: string, menuTypeId: string) =>
  `${menuPath(restaurantId)}/type/${menuTypeId}`;

export const menuDishPath = (restaurantId: string, dishId: string) =>
  `${menuPath(restaurantId)}/dish/${dishId}`;

export const orderPaths = (restaurantId: string, tableId: string) => {
  const root = `${tablePath(restaurantId, tableId)}/${ORDER_SEGMENT}`;
  return {
    root,
    menuType: (menuTypeId: string) => `${root}/type/${menuTypeId}`,
    dish: (dishId: string) => `${root}/dish/${dishId}`,
    cart: `${root}/cart`,
    tip: `${root}/cart/tip`,
    payment: `${root}/cart/payment`,
    confirmed: `${root}/confirmed`,
    failed: `${root}/failed`,
  };
};

const ORDERING_PATH_PATTERN = /\/tables\/[^/]+\/order(\/|$)/;

/** True for every screen in the ordering flow, including its checkout steps. */
export const isOrderingPath = (pathname: string): boolean =>
  ORDERING_PATH_PATTERN.test(pathname);

const ORDER_CHECKOUT_PATTERN = /\/tables\/[^/]+\/order\/cart\/(tip|payment)$/;

/** The two ordering steps that open the gradient drawer over a backdrop. */
export const isOrderCheckoutPath = (pathname: string): boolean =>
  ORDER_CHECKOUT_PATTERN.test(pathname);

const TABLE_SCOPE_PATTERN = /^\/restaurants\/([^/]+)\/tables\/([^/]+)/;

/**
 * Read the table scope straight off the URL, for code that sits outside the
 * route tree and therefore cannot use `useParams()` (see BackDropProvider,
 * which is mounted above the router in main.tsx).
 */
export const parseTableScope = (
  pathname: string,
): { restaurantId: string; tableId: string } | null => {
  const match = TABLE_SCOPE_PATTERN.exec(pathname);
  if (!match) return null;
  return { restaurantId: match[1], tableId: match[2] };
};
